import { isValidElement } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

import Button from '@/components/button/Button.jsx';
import { TONE_ICONS } from '@/components/status-pill/statusTones.js';

import './Progress.css';

/**
 * Progress.
 *
 * Two components, one status axis:
 *
 *   status   info (default) | success | warning | danger
 *
 * named and defaulted exactly as on Button, Alert and StatusBanner. Colour is not
 * written per state x status pair: every part reads ONE custom property that
 * `status` remaps — `--acc` on the bar, `--acc-ink` on the steps. That is what
 * makes "step 3 failed" a one-word change rather than a new set of rules.
 *
 * Status fills ground on the -ink tokens, never the raw hue: measured against the
 * --bg-card-3 track, raw --success is 1.90:1 and raw --warning 1.79:1 in light,
 * while the -ink versions clear 4.9:1 in both themes. Same conclusion, and the
 * same reason, as the block at the top of Button.css.
 *
 * ── Exports ─────────────────────────────────────────────────────────────────
 *   ProgressBar        determinate bar
 *   ProgressSteps      stepper; `appearance` picks bubbles or a segmented rail
 *   ProgressStepBar    ProgressSteps pinned to appearance="icon"
 *   ProgressLineSteps  ProgressSteps pinned to appearance="line"
 *
 * The last two are the original API and still take the original props —
 * `progress`, `labelRequired`, `isLabelRequired`, `labelArray`, `showDoneIcon`,
 * `validatedSteps`, and `steps` as either a list or a count.
 */

const cx = (...parts) => parts.filter(Boolean).join(' ');

const STATUSES = new Set(['info', 'success', 'warning', 'danger']);
const statusOf = (v) => (STATUSES.has(v) ? v : 'info');

/* Anything that isn't a finite number is 0 — an empty bar, not a full one. (An
   invalid `width` used to fall back to `auto`, so a missing value painted 100%.) */
const toNumber = (v) => {
    const n = typeof v === 'string' ? Number(v) : v;
    return typeof n === 'number' && Number.isFinite(n) ? n : 0;
};

/* ── Bar ──────────────────────────────────────────────────────────────────── */

const BAR_LABEL_POSITIONS = new Set([
    'right', 'left', 'top', 'bottom', 'inside-start', 'inside-center', 'inside-end',
]);

export const ProgressBar = ({
    value,
    progress,
    max = 100,
    status = 'info',
    size = 'medium',
    label,
    labelRequired = false,
    labelPosition = 'right',
    disabled = false,
    ariaLabel,
    className = '',
    ...props
}) => {
    const parsedMax = toNumber(max);
    const limit = parsedMax > 0 ? parsedMax : 100;
    const clamped = Math.min(Math.max(toNumber(value ?? progress), 0), limit);
    const pct = Math.round((clamped / limit) * 100);

    const wantsLabel = label ?? labelRequired;
    const labelNode = wantsLabel === true ? `${pct}%` : (wantsLabel || null);
    const hasLabel = labelNode != null;

    const pos = BAR_LABEL_POSITIONS.has(labelPosition) ? labelPosition : 'right';
    const isInside = pos.startsWith('inside');
    const tone = statusOf(status);

    /* Outside labels are ordinary flex siblings, so `top`/`left` render before the
       track and `bottom`/`right` after it — the CSS only picks the axis. */
    const outsideLabel = hasLabel && !isInside
        ? <span className="progress-value">{labelNode}</span>
        : null;

    return (
        <div
            className={cx(
                'progress',
                `progress--${size}`,
                tone !== 'info' && `progress--${tone}`,
                `progress--label-${pos}`,
                isInside && 'progress--inside',
                disabled && 'progress--disabled',
                className,
            )}
            {...props}
        >
            {(pos === 'top' || pos === 'left') && outsideLabel}

            <div
                className="progress-track"
                /* Drives the clip on the on-fill copy of an inside label. */
                style={isInside ? { '--pct': `${pct}%` } : undefined}
                role="progressbar"
                aria-label={ariaLabel || 'Progress'}
                aria-valuemin={0}
                aria-valuemax={limit}
                aria-valuenow={clamped}
                aria-valuetext={`${pct}%`}
            >
                <div className="progress-fill" style={{ width: `${pct}%` }} />
                {/* Inside labels are drawn twice and stacked: the second copy is
                    clipped to the filled region, so each half of the text is
                    coloured for the surface actually behind it. One copy alone
                    cannot clear contrast on both the fill and the bare track. */}
                {hasLabel && isInside && (
                    <>
                        <span className="progress-value progress-value--on-track">{labelNode}</span>
                        <span className="progress-value progress-value--on-fill" aria-hidden="true">
                            {labelNode}
                        </span>
                    </>
                )}
            </div>

            {(pos === 'bottom' || pos === 'right') && outsideLabel}
        </div>
    );
};

ProgressBar.propTypes = {
    // Current value. Non-numeric is 0.
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Original name for `value`.
    progress: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    max: PropTypes.number,
    status: PropTypes.oneOf(['info', 'success', 'warning', 'danger']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    // `true` renders the rounded percentage; a node renders that instead.
    label: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
    // Original name for `label`.
    labelRequired: PropTypes.bool,
    // Outside the track, or within it. Inside forces a track tall enough to hold it.
    labelPosition: PropTypes.oneOf([
        'right', 'left', 'top', 'bottom', 'inside-start', 'inside-center', 'inside-end',
    ]),
    disabled: PropTypes.bool,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
};

/* ── Steps ────────────────────────────────────────────────────────────────── */

/* A FontAwesome icon definition is a plain object carrying `iconName`, or the
   [prefix, name] tuple. Matches the check StatusPill uses. */
const isFaIcon = (v) => Array.isArray(v)
    || (typeof v === 'object' && v !== null && !isValidElement(v) && 'iconName' in v);

/* `icon` may be a component (the original shape: `() => <FontAwesomeIcon .../>`),
   a FontAwesome definition, or any node. A step without one falls back to its
   number instead of throwing — `<StepIcon/>` on an undefined icon used to take
   the whole page down with "Element type is invalid". */
const glyphFor = (icon, fallback) => {
    if (!icon) return fallback;
    if (isFaIcon(icon)) return <FontAwesomeIcon icon={icon} />;
    if (isValidElement(icon)) return icon;
    if (typeof icon === 'function') {
        const Icon = icon;
        return <Icon />;
    }
    return fallback;
};

/* `steps` is a list of step objects, and stays one however it arrives: a count
   becomes that many blank steps, so ProgressLineSteps' `steps={4}` and
   ProgressStepBar's `steps={[...]}` are the same thing downstream. `text` and
   `subText` are the original field names for `label` and `description`. */
const normalizeSteps = (steps, labelArray) => {
    const labelAt = (i) => (Array.isArray(labelArray) ? labelArray[i] : undefined);

    if (typeof steps === 'number') {
        const count = Number.isFinite(steps) ? Math.max(0, Math.floor(steps)) : 0;
        return Array.from({ length: count }, (_, i) => ({ label: labelAt(i) }));
    }
    if (!Array.isArray(steps)) return [];

    return steps.map((step, i) => (step && typeof step === 'object'
        ? {
            ...step,
            label: step.label ?? step.text ?? labelAt(i),
            description: step.description ?? step.subText,
        }
        : { label: step ?? labelAt(i) }));
};

const STEP_LABEL_POSITIONS = new Set(['bottom', 'top', 'left', 'right']);

export const ProgressSteps = ({
    steps,
    currentStep = 1,
    appearance = 'icon',
    orientation = 'horizontal',
    status = 'info',
    onStepClick,
    isStepClickable,
    showDoneIcon = false,
    validatedSteps = [],
    label,
    isLabelRequired,
    labelArray,
    labelPosition,
    disabled = false,
    ariaLabel,
    className = '',
    ...props
}) => {
    const items = normalizeSteps(steps, labelArray);
    const total = items.length;
    if (!total) return null;

    const validated = new Set(Array.isArray(validatedSteps) ? validatedSteps : []);
    const barStatus = statusOf(status);
    const isVertical = orientation === 'vertical';
    // Bubbles carry their own labels; the rail's are opt-in, as they always were.
    const showLabels = label ?? isLabelRequired ?? (appearance === 'icon');
    const position = Math.min(Math.max(currentStep, 1), total);

    /* Beside the bubble when the steps run down the page, beneath it when they run
       across — the arrangement that leaves the label room in each direction. */
    const pos = STEP_LABEL_POSITIONS.has(labelPosition)
        ? labelPosition
        : (isVertical ? 'right' : 'bottom');

    return (
        <div
            className={cx(
                'progress-steps',
                `progress-steps--${appearance}`,
                `progress-steps--label-${pos}`,
                isVertical && 'progress-steps--vertical',
                disabled && 'progress-steps--disabled',
                className,
            )}
            role="group"
            aria-label={ariaLabel || `Step ${position} of ${total}`}
            {...props}
        >
            {items.map((step, i) => {
                const n = i + 1;
                const isCurrent = n === currentStep;
                const isDone = n < currentStep || validated.has(n);
                const state = isCurrent ? 'current' : (isDone ? 'done' : 'upcoming');
                const stepDisabled = disabled || Boolean(step.disabled);

                /* One status per step: its own, else the bar's status applied to the
                   step the user is on — which is what turns the current step (and the
                   connector into it) red when Next fails validation. */
                const stepStatus = statusOf(step.status ?? (isCurrent ? barStatus : 'info'));

                /* The rail is a read-out, not a control, so only bubbles take clicks.
                   Un-gated by default, exactly as before; pass `isStepClickable` to
                   gate rather than re-checking inside `onStepClick`. A gated step is
                   not a control at all, while a DISABLED one still is — it renders as
                   a disabled button so it reads, and behaves, as unavailable. */
                const gated = typeof isStepClickable === 'function'
                    && !isStepClickable(n, state);
                const interactive = appearance === 'icon' && Boolean(onStepClick) && !gated;

                // Unchanged rule: a check replaces the icon only on a VALIDATED step.
                const showCheck = showDoneIcon && validated.has(n) && !isCurrent;
                const glyph = stepStatus !== 'info'
                    ? <FontAwesomeIcon icon={TONE_ICONS[stepStatus]} />
                    : (showCheck ? <FontAwesomeIcon icon={faCheck} /> : glyphFor(step.icon, n));

                const name = typeof step.label === 'string' ? step.label : `Step ${n}`;
                const mark = <span className="progress-step-glyph">{glyph}</span>;

                return (
                    <div
                        key={step.key ?? i}
                        className={cx(
                            'progress-step',
                            `progress-step--${state}`,
                            stepStatus !== 'info' && `progress-step--${stepStatus}`,
                            stepDisabled && 'progress-step--disabled',
                            /* Only meaningful where the other steps ARE clickable —
                               otherwise every step would read as withheld. */
                            gated && Boolean(onStepClick) && !stepDisabled && 'progress-step--gated',
                        )}
                    >
                        {appearance === 'line' ? (
                            <span className="progress-step-seg" />
                        ) : interactive ? (
                            <Button
                                variant="tertiary"
                                status={stepStatus}
                                className="progress-step-bubble"
                                aria-label={name}
                                aria-current={isCurrent ? 'step' : undefined}
                                disabled={stepDisabled}
                                onClick={() => onStepClick(n)}
                            >
                                {mark}
                            </Button>
                        ) : (
                            <span
                                className="progress-step-bubble"
                                aria-current={isCurrent ? 'step' : undefined}
                            >
                                {mark}
                            </span>
                        )}

                        {showLabels && (step.label || step.description) && (
                            <span className="progress-step-text">
                                {step.label && <span className="progress-step-label">{step.label}</span>}
                                {step.description && (
                                    <span className="progress-step-desc">{step.description}</span>
                                )}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const STEP_SHAPE = PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
    text: PropTypes.node,
    description: PropTypes.node,
    subText: PropTypes.node,
    icon: PropTypes.any,
    status: PropTypes.oneOf(['info', 'success', 'warning', 'danger']),
    disabled: PropTypes.bool,
});

ProgressSteps.propTypes = {
    // A list of steps, or a count when the steps carry nothing but their position.
    steps: PropTypes.oneOfType([PropTypes.arrayOf(STEP_SHAPE), PropTypes.number]),
    currentStep: PropTypes.number,
    // Numbered bubbles joined by connectors, or a flat segmented rail.
    appearance: PropTypes.oneOf(['icon', 'line']),
    // Vertical gives long labels room instead of pushing the row past its container.
    orientation: PropTypes.oneOf(['horizontal', 'vertical']),
    // Applied to the CURRENT step; per-step `status` overrides it.
    status: PropTypes.oneOf(['info', 'success', 'warning', 'danger']),
    onStepClick: PropTypes.func,
    // (stepNumber, state) => boolean. Gates which steps accept a click.
    isStepClickable: PropTypes.func,
    showDoneIcon: PropTypes.bool,
    validatedSteps: PropTypes.array,
    label: PropTypes.bool,
    // Original name for `label`.
    isLabelRequired: PropTypes.bool,
    // Original label source when `steps` is a count.
    labelArray: PropTypes.array,
    // Defaults to 'bottom' horizontally and 'right' vertically.
    labelPosition: PropTypes.oneOf(['bottom', 'top', 'left', 'right']),
    disabled: PropTypes.bool,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
};

/* ── Appearance exports ───────────────────────────────────────────────────────
   `appearance` is set AFTER the spread, so a ProgressStepBar stays bubbles even
   if a call site passes one. Every other prop still passes through, so
   `<ProgressLineSteps orientation="vertical" status="danger" />` works. */

const pinAppearance = (appearance, displayName) => {
    const Pinned = (props) => <ProgressSteps {...props} appearance={appearance} />;
    Pinned.displayName = displayName;
    return Pinned;
};

export const ProgressStepBar = pinAppearance('icon', 'ProgressStepBar');
export const ProgressLineSteps = pinAppearance('line', 'ProgressLineSteps');

export default ProgressBar;
